import React, { useState, useEffect } from "react";
import { Box, Text } from "@chakra-ui/react";
import api from "../api";
import { ProgressBar } from "./ProgressBar";
import { TaskNode } from "./TaskNode";
import { Loading } from "./Loading";
import utils from "../utils";
import { InstructionOptions } from "./InstructionOptions";
import { LaunchInstructionExternal } from "./LaunchInstructionExternal";
import clientStorage from "../clientStorage";
import { CustomizedAlert } from "./CustomizedAlert";
import { Toaster } from "./Toaster";
import { CustomizedConfetti } from "./CustomizedConfetti";

export const Instruction = () => {
  const [instructionKey, setInstructionKey] = useState("");
  const [loadingInstructionData, setLoadingInstructionData] = useState(false);
  const [instructionDataLoaded, setInstructionDataLoaded] = useState(false);
  const [instructionData, setInstructionData] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(null);
  const [progressPoints, setProgressPoints] = useState(0);
  const [completedProgressPoints, setCompletedProgressPoints] = useState(0);
  const [progressBarSize, setProgressBarSize] = useState("sm");
  const [toastConfig, setToastConfig] = useState(null);
  const [emphasizeRootTasks, setEmphasizeRootTasks] = useState(false);
  const [confettiReady, setConfettiReady] = useState(false);
  const [confettiOnComplete, setConfettiOnComplete] = useState(true);

  useEffect(() => {
    const fetchInstructionData = async () => {
      await api
        .getInstructionData(queryInstructionKey)
        .then((response) => {
          setCompletedSteps(response.data.progress.completedSteps);
          setInstructionData(JSON.parse(response.data.instructionData));
        })
        .catch(() => {
          setToastConfig({
            title: "Error",
            description:
              "An error has occurred retrieving the instruction data.",
            position: "top",
            status: "error",
            duration: null,
            isClosable: false,
          });
        })
        .finally(() => {
          setLoadingInstructionData(false);
        });
    };

    // Load the progress bar size from local storage if present.
    const progressBarSize = clientStorage.getProgressBarSize();
    if (progressBarSize !== null) {
      setProgressBarSize(progressBarSize);
    }

    // Load the show confetti on complete from local storage if present.
    const confetti = clientStorage.getConfettiOnComplete();
    if (confetti !== null) {
      setConfettiOnComplete(confetti === "true");
    }

    // Obtain the publish key from the query string.
    const queryInstructionKey = new URL(window.location.href).searchParams.get(
      "id"
    );

    if (queryInstructionKey !== null && queryInstructionKey !== "") {
      setInstructionKey(queryInstructionKey);

      // Get the instruction data.
      setLoadingInstructionData(true);
      fetchInstructionData();
    } else {
      setToastConfig({
        title: "Error",
        description: "No instruction key provided.",
        position: "top",
        status: "error",
        duration: null,
        isClosable: false,
      });
    }
  }, []);

  useEffect(() => {
    // Is there a better way to do this? I don't want this effect to
    // run until it has been populated - and it appears it runs when the
    // state is initialized/set to null.
    if (instructionData === null) {
      return;
    }

    // Set the HTML title to the name of the instructions.
    document.title = instructionData.name;

    if (instructionData.emphasizeRootTasks) {
      setEmphasizeRootTasks(true);
    }

    try {
      const calculatedCompletedSteps = calculateCompletedSteps(
        instructionData,
        completedSteps
      );
      setCompletedSteps(calculatedCompletedSteps);
      setInstructionDataLoaded(true);
    } catch (e) {
      setToastConfig({
        title: "Error",
        description:
          "Failed to load the instruction data. Please republish the instructions, or ask for them to be republished.",
        position: "top",
        status: "error",
        duration: null,
        isClosable: false,
      });

      console.log(e);
    }
  }, [instructionData]);

  useEffect(() => {
    // Is there a better way to do this? I don't want this effect to
    // run until it has been populated - and it appears it runs when the
    // state is initialized/set to null.
    if (completedSteps === null || instructionData === null) {
      return;
    }

    const saveProgress = async () => {
      await api
        .setUserProgress(instructionKey, { completedSteps: completedSteps })
        .catch(() => {
          setToastConfig({
            title: "Error",
            description: "An error has occurred saving your progress.",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        });
    };

    const [total, completed] = calculateProgressPoints(
      instructionData,
      completedSteps
    );
    setProgressPoints(total);
    setCompletedProgressPoints(completed);

    saveProgress();
  }, [completedSteps]);

  const handleTaskCheckChange = (taskId, completed) => {
    const calculatedCompletedSteps = calculateCompletedSteps(
      instructionData,
      completedSteps,
      taskId,
      completed
    );
    setCompletedSteps(calculatedCompletedSteps);
    setConfettiReady(true);
  };

  const handleProgressBarSizeChange = (size) => {
    setProgressBarSize(size);
    clientStorage.setProgressBarSize(size);
  };

  const handleConfettiOnCompleteChange = (value) => {
    setConfettiReady(false);
    setConfettiOnComplete(value);
    clientStorage.setConfettiOnComplete(value);
  };

  const instructionMenuSegment = utils.runningInIframe() ? (
    <LaunchInstructionExternal instructionKey={instructionKey} />
  ) : (
    <InstructionOptions
      progressBarSize={progressBarSize}
      confettiOnComplete={confettiOnComplete}
      onPrintInstructions={() => window.print()}
      onClearProgress={() => setCompletedSteps([])}
      onProgressBarSizeChange={handleProgressBarSizeChange}
      onConfettiOnCompleteChange={handleConfettiOnCompleteChange}
    />
  );

  const loadedInstruction = !instructionDataLoaded ? (
    loadingInstructionData ? (
      <Loading />
    ) : (
      ""
    )
  ) : (
    <Box>
      <CustomizedConfetti
        showConfetti={
          confettiReady &&
          completedProgressPoints > 0 &&
          progressPoints === completedProgressPoints &&
          confettiOnComplete
        }
      />
      <ProgressBar
        progressPoints={progressPoints}
        completedProgressPoints={completedProgressPoints}
        size={progressBarSize}
      />
      <Box marginTop={progressBarSize === "lg" ? "16px" : "8px"}>
        {!utils.runningInIframe() ? (
          <Text fontSize="2xl">{instructionData.name}</Text>
        ) : (
          ""
        )}
        {instructionData.notice !== "" ? (
          <Box mt={2} mb={2}>
            <CustomizedAlert status="warning" text={instructionData.notice} />
          </Box>
        ) : (
          ""
        )}
        <Text
          dangerouslySetInnerHTML={{
            __html: utils.formatDisplayText(instructionData.description),
          }}
        ></Text>
        {instructionData.tasks.map((task, index) => (
          <TaskNode
            index={index}
            key={task.id}
            incrementType="1"
            id={task.id}
            description={task.description}
            progressPoints={task.progressPoints}
            checkable={task.checkable}
            icon={task.icon}
            items={task.items}
            tasks={task.tasks}
            nestDepth={0}
            completedSteps={completedSteps}
            onTaskCheckChange={handleTaskCheckChange}
            emphasizeRootTasks={emphasizeRootTasks}
            instructionKey={instructionKey}
          />
        ))}
        <Text textAlign="center" pt={4} pb={4}>
          © {new Date().getFullYear()} Northcentral Technical College
        </Text>
      </Box>
      {instructionMenuSegment}
    </Box>
  );

  return (
    <Box>
      {loadedInstruction} <Toaster toastConfig={toastConfig} />
    </Box>
  );
};

const calculateProgressPoints = (instructionData, completedSteps) => {
  let progressObj = { progressPoints: 0, completedProgressPoints: 0 };

  for (let i = 0; i < instructionData.tasks.length; i++) {
    progressTask(instructionData.tasks[i], progressObj, completedSteps);
  }

  function progressTask(task, progressObj, tempCompletedSteps) {
    // Only "count" the task if it's checkable.
    if (task.checkable) {
      // Increment the progress points.
      progressObj.progressPoints += task.progressPoints;

      if (tempCompletedSteps.indexOf(task.id.toString()) !== -1) {
        // If the step is found in the list of completed steps, increment the
        // completed progress points.
        progressObj.completedProgressPoints += task.progressPoints;
      }
    }

    // Process child tasks.
    if (task.tasks.length > 0) {
      for (let i = 0; i < task.tasks.length; i++) {
        progressTask(task.tasks[i], progressObj, tempCompletedSteps);
      }
    }
  }

  return [progressObj.progressPoints, progressObj.completedProgressPoints];
};

const calculateCompletedSteps = (
  instructionData,
  completedSteps,
  forceTaskId = null,
  forceValue = null
) => {
  // Make a copy of the array in state.
  let tempCompletedSteps =
    completedSteps !== null ? Array.from(completedSteps) : [];

  if (forceTaskId !== null) {
    const completedTaskIndex = tempCompletedSteps.indexOf(forceTaskId);

    if (forceValue && completedTaskIndex < 0) {
      tempCompletedSteps.push(forceTaskId);
    } else if (!forceValue && completedTaskIndex > -1) {
      tempCompletedSteps.splice(completedTaskIndex, 1);
    }
  }

  for (let i = 0; i < instructionData.tasks.length; i++) {
    processTask(
      tempCompletedSteps,
      instructionData.tasks[i],
      forceTaskId,
      forceValue,
      instructionData.tasks[i].id.toString() === forceTaskId
    );
  }

  function processTask(
    tempCompletedSteps,
    task,
    forceTaskId = null,
    forceValue = null,
    applyForce = false
  ) {
    let taskCompleted = applyForce ? forceValue : true;

    if (task.tasks.length > 0) {
      for (let i = 0; i < task.tasks.length; i++) {
        processTask(
          tempCompletedSteps,
          task.tasks[i],
          forceTaskId,
          forceValue,
          applyForce || task.tasks[i].id.toString() === forceTaskId
        );

        if (applyForce) {
          taskCompleted = forceValue;
        } else {
          if (tempCompletedSteps.indexOf(task.tasks[i].id.toString()) === -1) {
            taskCompleted = false;
          }
        }
      }
    } else {
      if (applyForce) {
        taskCompleted = forceValue;
      } else {
        taskCompleted = tempCompletedSteps.indexOf(task.id.toString()) !== -1;
      }
    }

    const completedTaskIndex = tempCompletedSteps.indexOf(task.id.toString());
    if (taskCompleted && completedTaskIndex === -1) {
      tempCompletedSteps.push(task.id.toString());
    } else if (!taskCompleted && completedTaskIndex !== -1) {
      tempCompletedSteps.splice(completedTaskIndex, 1);
    }
  }

  return tempCompletedSteps;
};
